/**
 * ReportChart — light-theme chart wrappers used by the report viewer.
 *
 * All variants share a common color palette and tooltip styling so the
 * report feels coherent across donut / bar / stacked / line / scatter
 * / heatmap / radar.
 *
 * Recharts is the underlying library (already in the dependency tree).
 */

import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  LineChart, Line,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ScatterChart, Scatter, ZAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { type CSSProperties, type ReactNode } from 'react';

// Five-step palette — soft indigo → mint → amber → rose → slate.
// Picked for readable contrast on a #FAFAF7 background and to match
// the v2 design tokens.
export const CHART_PALETTE = [
  '#5B5BD6', // indigo (accent)
  '#10B981', // mint (success)
  '#F59E0B', // amber (warning)
  '#E11D48', // rose (danger)
  '#0EA5E9', // sky (info)
  '#A78BFA', // royal lilac
  '#14B8A6', // teal
  '#F472B6', // pink
];

const AXIS_COLOR = '#8A91A0';
const GRID_COLOR = 'rgba(14,17,22,0.06)';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number | string; color?: string; dataKey?: string }>;
  label?: string | number;
}

function ChartTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      style={{
        background: 'rgba(14,17,22,0.96)',
        color: '#FFFFFF',
        borderRadius: '6px',
        padding: '8px 12px',
        fontSize: '12px',
        boxShadow: '0 4px 16px rgba(14,17,22,0.18)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      {label !== undefined && (
        <div style={{ opacity: 0.6, marginBottom: 4, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </div>
      )}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          {p.color && (
            <span
              style={{
                width: 8, height: 8, borderRadius: 2,
                background: p.color,
                display: 'inline-block',
              }}
            />
          )}
          <span style={{ opacity: 0.7 }}>{p.name ?? p.dataKey}</span>
          <span style={{ marginLeft: 'auto', fontWeight: 600 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

const baseAxisProps = {
  stroke: AXIS_COLOR,
  fontSize: 11,
  tickLine: false,
  axisLine: false,
  tick: { fill: AXIS_COLOR },
};

// ── Donut ────────────────────────────────────────────────────────────────

interface DonutDatum { name: string; value: number; }

export function Donut({
  data,
  height = 240,
  centerLabel,
  centerValue,
}: {
  data: DonutDatum[];
  height?: number;
  centerLabel?: string;
  centerValue?: string;
}) {
  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            innerRadius="62%"
            outerRadius="92%"
            paddingAngle={2}
            dataKey="value"
            stroke="none"
          >
            {data.map((_, i) => (
              <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      {(centerLabel || centerValue) && (
        <div
          style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center', pointerEvents: 'none',
          }}
        >
          {centerValue && (
            <div
              className="font-display font-bold"
              style={{ fontSize: 24, color: 'var(--ch2-ink)', letterSpacing: '-0.02em' }}
            >
              {centerValue}
            </div>
          )}
          {centerLabel && (
            <div className="l-section-eyebrow" style={{ marginTop: 2 }}>
              {centerLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Legend list (custom — Recharts default looks heavy) ─────────────────

export function ChartLegend({ items }: { items: Array<{ name: string; value?: string | number; color?: string }> }) {
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={it.name} className="flex items-center gap-2 text-sm">
          <span
            style={{
              width: 8, height: 8, borderRadius: 2,
              background: it.color ?? CHART_PALETTE[i % CHART_PALETTE.length],
              flexShrink: 0,
            }}
          />
          <span className="flex-1 truncate" style={{ color: 'var(--ch2-ink-2)' }}>
            {it.name}
          </span>
          {it.value !== undefined && (
            <span className="font-semibold" style={{ color: 'var(--ch2-ink)' }}>
              {it.value}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

// ── Bar (vertical) ───────────────────────────────────────────────────────

interface BarDatum { name: string; value: number; }

export function VerticalBar({
  data,
  height = 240,
  color = CHART_PALETTE[0],
  valueFormatter,
}: {
  data: BarDatum[];
  height?: number;
  color?: string;
  valueFormatter?: (v: number) => string;
}) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
          <CartesianGrid stroke={GRID_COLOR} vertical={false} />
          <XAxis dataKey="name" {...baseAxisProps} />
          <YAxis tickFormatter={valueFormatter} {...baseAxisProps} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(91,91,214,0.08)' }} />
          <Bar dataKey="value" fill={color} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Horizontal bar (ranked lists) ───────────────────────────────────────

export function HorizontalBar({
  data,
  height = 240,
  color = CHART_PALETTE[0],
  valueFormatter,
}: {
  data: BarDatum[];
  height?: number;
  color?: string;
  valueFormatter?: (v: number) => string;
}) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid stroke={GRID_COLOR} horizontal={false} />
          <XAxis type="number" tickFormatter={valueFormatter} {...baseAxisProps} />
          <YAxis type="category" dataKey="name" width={120} {...baseAxisProps} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(91,91,214,0.08)' }} />
          <Bar dataKey="value" fill={color} radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Stacked bar (e.g. age × gender) ─────────────────────────────────────

interface StackDatum { name: string; [key: string]: string | number; }

export function StackedBar({
  data,
  series,
  height = 260,
}: {
  data: StackDatum[];
  series: Array<{ key: string; name: string; color?: string }>;
  height?: number;
}) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
          <CartesianGrid stroke={GRID_COLOR} vertical={false} />
          <XAxis dataKey="name" {...baseAxisProps} />
          <YAxis {...baseAxisProps} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(91,91,214,0.06)' }} />
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name}
              stackId="x"
              fill={s.color ?? CHART_PALETTE[i % CHART_PALETTE.length]}
              radius={i === series.length - 1 ? [6, 6, 0, 0] : 0}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Line / trend ────────────────────────────────────────────────────────

interface LineDatum { name: string; [key: string]: string | number; }

export function TrendLine({
  data,
  series,
  height = 220,
}: {
  data: LineDatum[];
  series: Array<{ key: string; name: string; color?: string }>;
  height?: number;
}) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
          <CartesianGrid stroke={GRID_COLOR} vertical={false} />
          <XAxis dataKey="name" {...baseAxisProps} />
          <YAxis {...baseAxisProps} />
          <Tooltip content={<ChartTooltip />} />
          {series.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color ?? CHART_PALETTE[i % CHART_PALETTE.length]}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Radar / score map ───────────────────────────────────────────────────

interface RadarDatum { axis: string; value: number; }

export function RadarScore({
  data,
  height = 260,
  color = CHART_PALETTE[0],
}: {
  data: RadarDatum[];
  height?: number;
  color?: string;
}) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke={GRID_COLOR} />
          <PolarAngleAxis dataKey="axis" tick={{ fill: AXIS_COLOR, fontSize: 11 }} />
          <PolarRadiusAxis tick={false} axisLine={false} />
          <Tooltip content={<ChartTooltip />} />
          <Radar
            dataKey="value"
            stroke={color}
            fill={color}
            fillOpacity={0.18}
            strokeWidth={2}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Scatter (peer benchmark) ────────────────────────────────────────────

interface ScatterDatum { x: number; y: number; name?: string; size?: number; isYou?: boolean; }

export function PeerScatter({
  data,
  xLabel,
  yLabel,
  height = 280,
  xFormatter,
  yFormatter,
}: {
  data: ScatterDatum[];
  xLabel?: string;
  yLabel?: string;
  height?: number;
  xFormatter?: (v: number) => string;
  yFormatter?: (v: number) => string;
}) {
  const peers = data.filter((d) => !d.isYou);
  const you = data.filter((d) => d.isYou);
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 16, right: 16, left: 8, bottom: 24 }}>
          <CartesianGrid stroke={GRID_COLOR} />
          <XAxis
            type="number" dataKey="x" name={xLabel}
            tickFormatter={xFormatter} {...baseAxisProps}
            label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -8, fill: AXIS_COLOR, fontSize: 11 } : undefined}
          />
          <YAxis
            type="number" dataKey="y" name={yLabel}
            tickFormatter={yFormatter} {...baseAxisProps}
            label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fill: AXIS_COLOR, fontSize: 11 } : undefined}
          />
          <ZAxis type="number" dataKey="size" range={[40, 240]} />
          <Tooltip content={<ChartTooltip />} cursor={{ strokeDasharray: '3 3' }} />
          <Scatter data={peers} fill={CHART_PALETTE[5]} fillOpacity={0.55} />
          <Scatter data={you} fill={CHART_PALETTE[0]} stroke={CHART_PALETTE[1]} strokeWidth={2} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Heatmap (e.g. posting timing day × hour) ─────────────────────────────

interface HeatmapCell { row: string; col: string; value: number; }

export function Heatmap({
  data,
  rows,
  cols,
  valueFormatter,
}: {
  data: HeatmapCell[];
  rows: string[];
  cols: string[];
  valueFormatter?: (v: number) => string;
}) {
  const lookup = new Map<string, number>();
  let max = 0;
  for (const c of data) {
    lookup.set(`${c.row}|${c.col}`, c.value);
    if (c.value > max) max = c.value;
  }
  const colorFor = (v: number): CSSProperties['background'] => {
    if (max === 0) return 'var(--ch2-surface-2)';
    const t = v / max;
    // indigo → white; t=1 is full, t=0 is empty.
    const r = Math.round(255 - (255 - 91) * t);
    const g = Math.round(255 - (255 - 91) * t);
    const b = Math.round(255 - (255 - 214) * t);
    return `rgb(${r}, ${g}, ${b})`;
  };
  return (
    <div className="overflow-x-auto">
      <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 2 }}>
        <thead>
          <tr>
            <th />
            {cols.map((col) => (
              <th key={col} className="px-1 py-1 font-medium" style={{ color: 'var(--ch2-ink-3)' }}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row}>
              <th className="pr-2 text-right font-medium" style={{ color: 'var(--ch2-ink-3)' }}>{row}</th>
              {cols.map((col) => {
                const v = lookup.get(`${row}|${col}`) ?? 0;
                return (
                  <td
                    key={col}
                    title={valueFormatter ? valueFormatter(v) : String(v)}
                    style={{
                      background: colorFor(v),
                      width: 28, height: 28, minWidth: 28,
                      borderRadius: 4,
                      border: '1px solid rgba(14,17,22,0.04)',
                    }}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── KPI value with subtitle (smaller than StatCard) ─────────────────────

export function KpiValue({ value, label, hint }: { value: ReactNode; label: string; hint?: string }) {
  return (
    <div>
      <div className="l-section-eyebrow">{label}</div>
      <div
        className="font-display font-bold mt-1"
        style={{ fontSize: 22, color: 'var(--ch2-ink)', letterSpacing: '-0.02em' }}
      >
        {value}
      </div>
      {hint && <div className="l-subtle text-xs mt-0.5">{hint}</div>}
    </div>
  );
}
